import { ThemeOptions } from '@mui/material/styles';
import { TournamentBracketMatchGameStatus, TournamentTeamStatus } from '@okletsplay/core';
import { GameSessionPlayerStatus } from '@okletsplay/core/src/modules/game-session/enums';
import { toxLinkModalOverrides } from 'app/theme/tox-link-modal-overrides';
import { MUI_LINK_OVERRIDES } from './mui-link-overrides';
import { MUI_TAB_OVERRIDES } from './mui-tab-overrides';
import { MUI_TABS_OVERRIDES } from './mui-tabs-overrides';
import { MUI_BUTTON_OVERRIDES } from './mui-button-overrides';
import { MUI_TOOLTIP_OVERRIDES } from './mui-tooltip-overrides';
import { MUI_TYPOGRAPHY_VARIANTS_OVERRIDES } from './mui-typography-overrides';

export const DEFAULT_THEME: ThemeOptions = {
  typography: {
    fontFamily: ['"Noto Sans"', '"Bebas Neue", "Roboto"'].join(','),
    fontSize: 12.25,
    subtitle1: {
      fontSize: 14,
    },
    subtitle2: {
      fontSize: 12,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 10,
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#6e03f6',
      dark: '#751ee5',
      light: '#a2a5d7',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2c2e33',
      dark: '#55577a',
      light: '#a7b0b5',
    },
    sidebar: {
      background: {
        main: '#222327',
        hover: '#2c2e33',
      },
      text: {
        main: '#a7b0b5',
        disabled: '#616161',
      },
    },
    lobby: {
      background: {
        inviteNotification: '#121315',
      },
      team: {
        status: {
          [TournamentTeamStatus.Pending]: '#616161',
          [TournamentTeamStatus.Standby]: '#7baa64',
          [TournamentTeamStatus.Checkin]: '#f5ce02',
          [TournamentTeamStatus.Ready]: '#7baa64',
          [TournamentTeamStatus.Active]: '#7baa64',
          [TournamentTeamStatus.Inactive]: '#616161',
          [TournamentTeamStatus.Eliminated]: '#f5ce02',
          [TournamentTeamStatus.Disqualified]: '#C16060',
        },
      },
    },
    bracket: {
      matchTitle: '#55577a',
      champion: '#55577a',
      link: '#55577a',
      status: {
        [TournamentBracketMatchGameStatus.Running]: '#6e03f6',
        [TournamentBracketMatchGameStatus.Locked]: '#616161',
        [TournamentBracketMatchGameStatus.Ready]: '#0095d3',
        [TournamentBracketMatchGameStatus.Archived]: '#7baa64',
        [TournamentBracketMatchGameStatus.Waiting]: '#616161',
        [TournamentBracketMatchGameStatus.Completed]: '#7baa64',
      },
    },
    gameSession: {
      statuses: {
        [GameSessionPlayerStatus.Pending]: '#BDBDBF',
        [GameSessionPlayerStatus.Joined]: '#7BAA64',
        [GameSessionPlayerStatus.Ready]: '#559897',
        [GameSessionPlayerStatus.Left]: '#C16060',
      },
    },
    progressBar: {
      pending: '#f5ce02',
      live: '#7baa64',
      finished: '#a2a5d7',
      canceled: '#a2a5d7',
      default: '#3c3f5d',
    },
    background: {
      default: 'rgba(27,28,31,0.6)',
      paper: '#222327',
    },
    divider: '#171718',
    modal: {
      default: '#1c1c1e',
      paper: '#222327',
      active: '#2b2d32',
      light: '#26272a',
    },
    notification: {
      tile: {
        unread: '#464950',
        hover: '#3a0085',
      },
    },
    action: {
      active: '#fff',
      hover: '#a7b0b5',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
    },
    text: {
      primary: '#fff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    bannerTimer: {
      text: '#FFFFFF',
      background: '#1B1C1F99',
    },
    game: {
      background: '#3D3F5E',
      carousel: {
        item: {
          background: '#303241',
        },
      },
    },
    alert: {
      background: '#3f4146',
      text: {
        primary: '#ffffff',
        secondary: '#eaeaea',
      },
    },
    success: {
      main: '#399a98',
      light: '#7baa64',
    },
    info: {
      main: '#0095d3',
    },
    warning: {
      light: '#f5ce02',
      main: '#fa8800',
    },
    error: {
      main: '#e62700',
      light: '#c16060',
    },
    black: '#000000',
  },
  breakpoints: {
    values: {
      xs: 0,
      'x-xs': 450,
      sm: 650,
      md: 700,
      lg: 1300,
      'x-lg': 1450,
      xl: 1536,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        'body a': {
          color: theme.palette.primary.light,
          textDecoration: 'underline',

          '&:hover': {
            color: theme.palette.primary.light,
            textDecoration: 'none',
          },
        },
        ...toxLinkModalOverrides(theme),
      }),
    },
    MuiTabs: {
      styleOverrides: MUI_TABS_OVERRIDES,
    },
    MuiTab: {
      styleOverrides: MUI_TAB_OVERRIDES,
    },
    MuiLink: {
      styleOverrides: MUI_LINK_OVERRIDES,
    },
    MuiButton: {
      styleOverrides: MUI_BUTTON_OVERRIDES,
    },
    MuiTooltip: {
      styleOverrides: MUI_TOOLTIP_OVERRIDES,
    },
    MuiTypography: {
      variants: MUI_TYPOGRAPHY_VARIANTS_OVERRIDES,
    },
  },
  zIndex: {
    modal: 1000,
  },
};
